/**
 * Catálogo de clientes y proyectos.
 */
import React, { useMemo, useState } from 'react';
import { initialClientState, initialProjectState } from '../AdminPanel';
import TrashIcon from '../../assets/icons/trash.svg';
import EditIcon from '../../assets/icons/edit.svg';
import FolderIcon from '../../assets/icons/folder.svg';
import { studio, CatalogCard, StudioIcon, SearchField, Field } from './studioUi';
import { useAuth } from '../../AuthContext';

const ClientProjectManagement = ({
    clients,
    projects,
    biblioteca,
    cargarTodo,
    userPermissions,
    fetchWithAuth,
}) => {
    const { withBusy } = useAuth();
    const API_URL_INTERNAL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    const [nuevoCliente, setNuevoCliente] = useState(initialClientState);
    const [editandoClienteId, setEditandoClienteId] = useState(null);
    const [showClientForm, setShowClientForm] = useState(false);
    const [nuevoProyecto, setNuevoProyecto] = useState(initialProjectState);
    const [editandoProyectoId, setEditandoProyectoId] = useState(null);
    const [showProjectForm, setShowProjectForm] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [query, setQuery] = useState('');

    const search = query.trim().toLowerCase();
    const reportCount = (biblioteca || []).length;

    const projectCounts = useMemo(() => (projects || []).reduce((acc, project) => {
        acc[project.clientId] = (acc[project.clientId] || 0) + 1;
        return acc;
    }, {}), [projects]);

    const reportCounts = useMemo(() => (biblioteca || []).reduce((acc, report) => {
        if (report.projectId) acc[report.projectId] = (acc[report.projectId] || 0) + 1;
        return acc;
    }, {}), [biblioteca]);

    const handleClientSubmit = async (e) => {
        e.preventDefault();
        try {
            await withBusy(async () => {
            const url = editandoClienteId ? `${API_URL_INTERNAL}/admin/clients/${editandoClienteId}` : `${API_URL_INTERNAL}/admin/clients`;
            const method = editandoClienteId ? 'PUT' : 'POST';
            const res = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(nuevoCliente),
            });
            if (res.ok) {
                alert('Cliente guardado con éxito.');
                setNuevoCliente(initialClientState);
                setEditandoClienteId(null);
                setShowClientForm(false);
                cargarTodo();
            } else {
                const d = await res.json().catch(() => ({ error: 'Error desconocido al guardar cliente.' }));
                alert(d.error);
            }
            }, 'Guardando cliente…');
        } catch (error) {
            alert(`Error de conexión: ${error.message}`);
        }
    };

    const handleEditClient = (client) => {
        setEditandoClienteId(client.id);
        setNuevoCliente(client);
        setShowClientForm(true);
    };

    const cancelarEdicionCliente = () => {
        setEditandoClienteId(null);
        setNuevoCliente(initialClientState);
        setShowClientForm(false);
    };

    const handleProjectSubmit = async (e) => {
        e.preventDefault();
        if (!nuevoProyecto.clientId || !nuevoProyecto.nombre) {
            return alert('Debes seleccionar un cliente y proporcionar un nombre para el proyecto.');
        }
        try {
            await withBusy(async () => {
            const url = editandoProyectoId ? `${API_URL_INTERNAL}/admin/projects/${editandoProyectoId}` : `${API_URL_INTERNAL}/admin/projects`;
            const method = editandoProyectoId ? 'PUT' : 'POST';
            const res = await fetchWithAuth(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(nuevoProyecto),
            });
            if (res.ok) {
                alert('Proyecto guardado con éxito.');
                setNuevoProyecto(initialProjectState);
                setEditandoProyectoId(null);
                setShowProjectForm(false);
                cargarTodo();
            } else {
                const d = await res.json().catch(() => ({ error: 'Error desconocido al guardar proyecto.' }));
                alert(d.error);
            }
            }, 'Guardando proyecto…');
        } catch (error) {
            alert(`Error de conexión: ${error.message}`);
        }
    };

    const handleEditProject = (project) => {
        setEditandoProyectoId(project.id);
        setNuevoProyecto(project);
        setShowProjectForm(true);
    };

    const cancelarEdicionProyecto = () => {
        setEditandoProyectoId(null);
        setNuevoProyecto(initialProjectState);
        setShowProjectForm(false);
    };

    const visibleClients = useMemo(() => {
        const list = clients || [];
        if (!search) return list;
        return list.filter((c) => (c.nombre || '').toLowerCase().includes(search));
    }, [clients, search]);

    if (selectedClient) {
        const projectsForSelectedClient = (projects || []).filter((p) => p.clientId === selectedClient.id);
        const visibleProjects = search
            ? projectsForSelectedClient.filter((p) => (p.nombre || '').toLowerCase().includes(search))
            : projectsForSelectedClient;

        return (
            <div style={studio.page}>
                <div style={studio.header}>
                    <div>
                        <p style={studio.kicker}>Catálogo</p>
                        <h2 style={studio.title}>{selectedClient.nombre}</h2>
                        <p style={studio.subtitle}>Proyectos de este cliente y cuántos reportes tiene cada uno.</p>
                        <div style={studio.crumbBar}>
                            <button type="button" onClick={() => { setSelectedClient(null); setQuery(''); setShowProjectForm(false); }} style={studio.crumbBtn}>Clientes</button>
                            <span style={studio.crumbSep}>/</span>
                            <span style={studio.crumbHere}>{selectedClient.nombre}</span>
                        </div>
                    </div>
                    <div style={studio.stats}>
                        <div style={studio.stat}>
                            <span style={studio.statValue}>{projectsForSelectedClient.length}</span>
                            <span style={studio.statLabel}>Proyectos</span>
                        </div>
                    </div>
                </div>

                <div style={studio.card}>
                    <div style={studio.toolbar}>
                        <h3 style={{ ...studio.formTitle, margin: 0 }}>Proyectos</h3>
                        <div style={{ display: 'flex', gap: 10 }}>
                            {!showProjectForm && userPermissions.projects_create && (
                                <button
                                    type="button"
                                    onClick={() => { setNuevoProyecto({ ...initialProjectState, clientId: selectedClient.id }); setShowProjectForm(true); }}
                                    style={studio.primaryBtn}
                                >
                                    Nuevo proyecto
                                </button>
                            )}
                            <button type="button" onClick={() => { setSelectedClient(null); setQuery(''); setShowProjectForm(false); }} style={studio.ghostBtn}>
                                Volver
                            </button>
                        </div>
                    </div>
                    {showProjectForm && (
                        <div style={studio.formPanel}>
                            <h3 style={studio.formTitle}>{editandoProyectoId ? 'Editar proyecto' : 'Nuevo proyecto'}</h3>
                            <form onSubmit={handleProjectSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <Field label="Nombre">
                                    <input placeholder="Nombre del proyecto" value={nuevoProyecto.nombre} onChange={(e) => setNuevoProyecto({ ...nuevoProyecto, nombre: e.target.value })} style={studio.control} required />
                                </Field>
                                <Field label="Logo">
                                    <input placeholder="URL del logo (opcional)" value={nuevoProyecto.logoUrl || ''} onChange={(e) => setNuevoProyecto({ ...nuevoProyecto, logoUrl: e.target.value })} style={studio.control} />
                                </Field>
                                <Field label="Repositorio">
                                    <input placeholder="URL del repositorio (opcional)" value={nuevoProyecto.repositorio || ''} onChange={(e) => setNuevoProyecto({ ...nuevoProyecto, repositorio: e.target.value })} style={studio.control} />
                                </Field>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <button type="submit" style={studio.primaryBtn}>{editandoProyectoId ? 'Actualizar' : 'Añadir'}</button>
                                    <button type="button" onClick={cancelarEdicionProyecto} style={studio.ghostBtn}>Cancelar</button>
                                </div>
                            </form>
                        </div>
                    )}
                    <SearchField value={query} onChange={setQuery} placeholder="Buscar proyecto…" style={{ maxWidth: 420, marginBottom: 16 }} />
                    {visibleProjects.length === 0 ? (
                        <div style={studio.empty}>{projectsForSelectedClient.length === 0 ? 'Este cliente aún no tiene proyectos.' : 'Ningún proyecto coincide.'}</div>
                    ) : (
                        <div style={studio.entityGrid}>
                            {visibleProjects.map((p) => (
                                <CatalogCard
                                    key={p.id}
                                    name={p.nombre}
                                    meta={`${reportCounts[p.id] || 0} ${(reportCounts[p.id] || 0) === 1 ? 'reporte' : 'reportes'}`}
                                    logoUrl={p.logoUrl}
                                    actions={(
                                        <>
                                            {p.repositorio && (
                                                <a title="Abrir repositorio" href={p.repositorio} target="_blank" rel="noopener noreferrer" style={studio.iconBtn}>
                                                    <StudioIcon src={FolderIcon} alt="Repositorio" />
                                                </a>
                                            )}
                                            {userPermissions.projects_edit && (
                                                <button type="button" title="Editar proyecto" onClick={() => handleEditProject(p)} style={studio.iconBtn}>
                                                    <StudioIcon src={EditIcon} alt="Editar" />
                                                </button>
                                            )}
                                            {userPermissions.projects_delete && (
                                                <button
                                                    type="button"
                                                    title="Borrar proyecto"
                                                    onClick={async () => {
                                                        if (!window.confirm(`¿Borrar el proyecto "${p.nombre}"?`)) return;
                                                        try {
                                                            const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/projects/${p.id}`, { method: 'DELETE' });
                                                            if (res.ok) {
                                                                alert('Proyecto borrado con éxito.');
                                                                cargarTodo();
                                                            } else {
                                                                const errorData = await res.json();
                                                                alert(errorData.error || 'Ocurrió un error al borrar el proyecto.');
                                                            }
                                                        } catch (error) {
                                                            alert(`Error de conexión: ${error.message}`);
                                                        }
                                                    }}
                                                    style={studio.iconBtnDanger}
                                                >
                                                    <StudioIcon src={TrashIcon} alt="Eliminar" />
                                                </button>
                                            )}
                                        </>
                                    )}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div style={studio.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Catálogo</p>
                    <h2 style={studio.title}>Clientes y proyectos</h2>
                    <p style={studio.subtitle}>Organiza las cuentas y entra a cada cliente para ver sus proyectos.</p>
                </div>
                <div style={studio.stats}>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{(clients || []).length}</span>
                        <span style={studio.statLabel}>Clientes</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{(projects || []).length}</span>
                        <span style={studio.statLabel}>Proyectos</span>
                    </div>
                    <div style={studio.stat}>
                        <span style={studio.statValue}>{reportCount}</span>
                        <span style={studio.statLabel}>Reportes</span>
                    </div>
                </div>
            </div>

            <div style={studio.card}>
                <div style={studio.toolbar}>
                    <h3 style={{ ...studio.formTitle, margin: 0 }}>Clientes</h3>
                    {!showClientForm && userPermissions.clients_create && (
                        <button type="button" onClick={() => setShowClientForm(true)} style={studio.primaryBtn}>Nuevo cliente</button>
                    )}
                </div>
                {showClientForm && (
                    <div style={studio.formPanel}>
                        <h3 style={studio.formTitle}>{editandoClienteId ? 'Editar cliente' : 'Nuevo cliente'}</h3>
                        <form onSubmit={handleClientSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <Field label="Nombre">
                                <input placeholder="Nombre del cliente" value={nuevoCliente.nombre} onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombre: e.target.value })} style={studio.control} required />
                            </Field>
                            <Field label="Logo">
                                <input placeholder="URL del logo (opcional)" value={nuevoCliente.logoUrl || ''} onChange={(e) => setNuevoCliente({ ...nuevoCliente, logoUrl: e.target.value })} style={studio.control} />
                            </Field>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="submit" style={studio.primaryBtn}>{editandoClienteId ? 'Actualizar' : 'Añadir'}</button>
                                <button type="button" onClick={cancelarEdicionCliente} style={studio.ghostBtn}>Cancelar</button>
                            </div>
                        </form>
                    </div>
                )}
                <SearchField value={query} onChange={setQuery} placeholder="Buscar cliente…" style={{ maxWidth: 420, marginBottom: 16 }} />
                {visibleClients.length === 0 ? (
                    <div style={studio.empty}>{(clients || []).length === 0 ? 'Aún no hay clientes.' : 'Ningún cliente coincide.'}</div>
                ) : (
                    <div style={studio.entityGrid}>
                        {visibleClients.map((c) => (
                            <CatalogCard
                                key={c.id}
                                name={c.nombre}
                                meta={`${projectCounts[c.id] || 0} ${(projectCounts[c.id] || 0) === 1 ? 'proyecto' : 'proyectos'}`}
                                logoUrl={c.logoUrl}
                                onOpen={() => { setSelectedClient(c); setQuery(''); }}
                                actions={(
                                    <>
                                        {userPermissions.clients_edit && (
                                            <button type="button" title="Editar cliente" onClick={() => handleEditClient(c)} style={studio.iconBtn}>
                                                <StudioIcon src={EditIcon} alt="Editar" />
                                            </button>
                                        )}
                                        {userPermissions.clients_delete && (
                                            <button
                                                type="button"
                                                title="Borrar cliente"
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    if (!window.confirm(`¿Borrar el cliente "${c.nombre}"?`)) return;
                                                    try {
                                                        const res = await fetchWithAuth(`${API_URL_INTERNAL}/admin/clients/${c.id}`, { method: 'DELETE' });
                                                        if (res.ok) {
                                                            alert('Cliente borrado con éxito.');
                                                            cargarTodo();
                                                        } else {
                                                            const errorData = await res.json();
                                                            alert(errorData.error || 'Ocurrió un error al borrar el cliente.');
                                                        }
                                                    } catch (error) {
                                                        alert(`Error de conexión: ${error.message}`);
                                                    }
                                                }}
                                                style={studio.iconBtnDanger}
                                            >
                                                <StudioIcon src={TrashIcon} alt="Eliminar" />
                                            </button>
                                        )}
                                    </>
                                )}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientProjectManagement;
